import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Check, Download, Mail, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { GoldDots } from "@/components/Brand";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { KETE, PLAN_CHECKS } from "@/lib/content";
import { errMsg, formatDate, useApi } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { downloadIcs, googleCalUrl } from "@/lib/calendar";
import { HeldPlan } from "@/components/HeldPlan";

const Check1 = ({ plan, part, onConfirm }) => {
  const done = plan.confirmations?.[part];
  return (
    <div className="mt-5 pt-4 border-t border-rule flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid={`check-${part}`}>
      <p className="text-sm text-ink-soft">{PLAN_CHECKS[part]}</p>
      {done ? (
        <span className="inline-flex items-center gap-2 text-sm font-medium text-sage-deep" data-testid={`check-${part}-confirmed`}><Check size={16} />Yes, that's right</span>
      ) : (
        <button className="btn-outline !min-h-[40px] !py-2 !px-5 text-sm" onClick={() => onConfirm(part)} data-testid={`check-${part}-yes-btn`}>Yes, that's right</button>
      )}
    </div>
  );
};

const Section = ({ label, children, testId }) => (
  <section className="ahu-card p-6 sm:p-8 fade-in" data-testid={testId}>
    <p className="eyebrow mb-4">{label}</p>
    {children}
  </section>
);

const ActionRow = ({ text, done, onToggle, i }) => (
  <li className="flex gap-4 py-4 border-b border-rule last:border-0">
    <button onClick={onToggle} aria-pressed={done} data-testid={`action-${i}-toggle`}
      className={`shrink-0 h-9 px-3 rounded-full text-xs font-medium border ${done ? "bg-sage border-sage text-white" : "bg-white border-rule text-ink-soft hover:border-charcoal"}`}
      style={{ transition: "background-color .35s ease, border-color .35s ease, color .35s ease" }}>
      {done ? <span className="inline-flex items-center gap-1"><Check size={14} />Done</span> : "To do"}
    </button>
    <p className={`leading-relaxed ${done ? "text-ink-soft" : "text-ink"}`}>{text}</p>
  </li>
);

const CheckinSetter = ({ onSet }) => {
  const [amount, setAmount] = useState(2);
  const [unit, setUnit] = useState("weeks");
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="checkin-setter">
      <span className="text-sm text-ink-soft">Check in with me in</span>
      <select className="field !w-20 !py-2" value={amount} onChange={(e) => setAmount(+e.target.value)} data-testid="checkin-amount-select">
        {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => <option key={n}>{n}</option>)}
      </select>
      <select className="field !w-32 !py-2" value={unit} onChange={(e) => setUnit(e.target.value)} data-testid="checkin-unit-select">
        {["days", "weeks", "months"].map((u) => <option key={u}>{u}</option>)}
      </select>
      <button className="btn-charcoal !min-h-[42px] !py-2" onClick={() => onSet(amount, unit)} data-testid="checkin-set-btn">Set check-in</button>
    </div>
  );
};

export default function PlanView() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const api = useApi();
  const nav = useNavigate();
  const { me, refresh } = useMe();
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState("");
  const name = me?.profile?.preferred_name || me?.profile?.first_name || "";

  useEffect(() => {
    api.get(`/plans/${id}`).then((r) => setPlan(r.data)).catch(() => { toast.error("We couldn't find that plan"); nav("/plans"); });
  }, [api, id, nav]);

  const act = async (fn, msg) => {
    try { const { data } = await fn(); if (data?.id) setPlan(data); if (msg) toast.success(msg); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const post = (path, body, msg) => act(() => api.post(`/plans/${id}/${path}`, body), msg);

  const download = async () => {
    setBusy("pdf");
    try {
      const r = await api.get(`/plans/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `AHU-Action-Plan-${plan.ref}.pdf` });
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("The PDF couldn't be made just now"); }
    setBusy("");
  };

  const email = async () => {
    setBusy("email");
    try { await api.post(`/plans/${id}/email`, { origin_url: window.location.origin }); toast.success(`Sent to ${me?.email}`); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };

  const save = async () => {
    await act(() => api.post(`/plans/${id}/save`), "Your AHU Plan is saved");
    nav("/");
  };

  const remove = async () => {
    await act(() => api.delete(`/plans/${id}`), "Action Plan deleted");
    nav("/plans");
  };

  if (!plan) return <AppShell><div className="py-24 flex justify-center"><GoldDots /></div></AppShell>;
  if (plan.payment_hold) return <HeldPlan plan={plan} />;
  const c = plan.content;
  const today = new Date().toISOString().slice(0, 10);
  const accOpen = plan.checkin_date && plan.checkin_date <= today;
  const isNew = params.get("new") === "1" && !plan.saved;

  return (
    <AppShell>
      <div className="fade-in mb-8" data-testid="plan-view">
        <p className="eyebrow mb-3" data-testid="plan-area">{plan.area}</p>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl leading-tight" data-testid="plan-title">{plan.title}</h1>
        <p className="mt-3 text-sm text-ink-soft">Created {formatDate(plan.created_at)} · <span className={plan.status === "completed" ? "text-sage-deep" : ""} data-testid="plan-status">{plan.status === "completed" ? "Completed" : "Active"}</span> · <span data-testid="plan-ref">Ref {plan.ref}</span></p>
      </div>
      <div className="space-y-5">
        {c.opening && (
          <section className="py-2 fade-in" data-testid="plan-opening">
            <p className="font-display text-2xl text-charcoal">Kia ora {name},</p>
            <p className="mt-3 text-lg leading-relaxed text-ink">{c.opening}</p>
          </section>
        )}
        <Section label="The Mirror" testId="plan-mirror">
          <p className="font-display text-xl sm:text-[22px] leading-relaxed text-charcoal">{c.mirror}</p>
          <Check1 plan={plan} part="mirror" onConfirm={(p) => post("confirm", { part: p })} />
        </Section>
        <Section label="1 Priority" testId="plan-priority">
          <p className="text-lg leading-relaxed">{c.priority}</p>
          <Check1 plan={plan} part="priority" onConfirm={(p) => post("confirm", { part: p })} />
        </Section>
        <Section label="1 Behavioural Shift" testId="plan-shift">
          <p className="text-lg leading-relaxed">{c.shift}</p>
          <Check1 plan={plan} part="shift" onConfirm={(p) => post("confirm", { part: p })} />
        </Section>
        <Section label="3 Actions" testId="plan-actions">
          <ul>{c.actions.map((t, i) => <ActionRow key={i} i={i} text={t} done={plan.actions_done?.[i]} onToggle={() => post("action", { index: i, done: !plan.actions_done?.[i] })} />)}</ul>
          <Check1 plan={plan} part="actions" onConfirm={(p) => post("confirm", { part: p })} />
        </Section>
        <Section label="1 Accountability" testId="plan-accountability">
          <p className="text-lg leading-relaxed">{c.accountability}</p>
          <div className="mt-6 rounded-xl bg-alabaster p-4 space-y-4">
            {plan.checkin_date ? (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className="font-medium text-charcoal" data-testid="checkin-date">Your check-in: {formatDate(plan.checkin_date, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
                <div className="flex flex-wrap gap-3 text-sm">
                  <button className="btn-quiet" onClick={() => downloadIcs(plan)} data-testid="add-calendar-btn"><CalendarPlus size={16} />Add to calendar</button>
                  <a className="btn-quiet" href={googleCalUrl(plan)} target="_blank" rel="noopener noreferrer" data-testid="google-calendar-link">Google Calendar</a>
                </div>
              </div>
            ) : <p className="text-sm text-ink-soft">Set a check-in so you know when to come back.</p>}
            <CheckinSetter onSet={(amount, unit) => post("checkin", { amount, unit }, "Check-in set")} />
            {!me?.is_member && me?.role !== "admin" && <p className="text-xs text-ink-soft">Email reminders on your check-in are part of membership.</p>}
            <div className="flex items-center gap-3 pt-1">
              <button disabled={!accOpen && !plan.accountability_done} onClick={() => post("accountability", { done: !plan.accountability_done })} data-testid="accountability-toggle"
                className={`h-9 px-4 rounded-full text-xs font-medium border disabled:opacity-50 ${plan.accountability_done ? "bg-sage border-sage text-white" : "bg-white border-rule text-ink-soft"}`}>
                {plan.accountability_done ? "Done" : "To do"}
              </button>
              <span className="text-sm text-ink-soft">{plan.accountability_done ? "Accountability kept" : accOpen ? "Mark it done once you've checked in with yourself" : "Opens on your check-in date"}</span>
            </div>
          </div>
          <Check1 plan={plan} part="accountability" onConfirm={(p) => post("confirm", { part: p })} />
        </Section>
        {c.kete_reflection && (
          <Section label="Your Kete Reflection" testId="plan-kete-reflection">
            <p className="text-lg leading-relaxed">{c.kete_reflection}</p>
          </Section>
        )}
        {c.closing && (
          <section className="py-4 fade-in" data-testid="plan-closing">
            <p className="font-display text-xl sm:text-2xl leading-relaxed text-charcoal">{c.closing}</p>
          </section>
        )}
        {plan.baskets && (
          <Section label="Your Three Baskets" testId="plan-baskets">
            <div className="grid gap-4 sm:grid-cols-3">
              {KETE.map((k) => (
                <div key={k.key} className="rounded-xl bg-alabaster p-4">
                  <p className="font-display text-charcoal">{k.name}</p>
                  <p className="text-sm text-ink-soft mt-1">{plan.baskets[k.key]}</p>
                </div>
              ))}
            </div>
          </Section>
        )}
      </div>
      <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        {isNew && <button className="btn-gold sm:min-w-[220px]" onClick={save} data-testid="save-plan-btn">Save my AHU Plan</button>}
        <button className="btn-outline" onClick={download} disabled={busy === "pdf"} data-testid="download-pdf-btn"><Download size={17} />{busy === "pdf" ? "Preparing" : "Download PDF"}</button>
        <button className="btn-outline" onClick={email} disabled={busy === "email"} data-testid="email-plan-btn"><Mail size={17} />{busy === "email" ? "Sending" : "Email my plan"}</button>
        {plan.status === "active" && <button className="btn-outline" onClick={() => post("status", { status: "completed" }, "Plan marked completed")} data-testid="complete-plan-btn">Mark plan completed</button>}
      </div>
      <div className="mt-8">
        <ConfirmDialog title="Delete this Action Plan?" body="This removes the plan from your account. It can't be undone." confirmLabel="Delete Action Plan" onConfirm={remove}
          trigger={<button className="text-sm text-warn underline underline-offset-4" data-testid="delete-plan-btn">Delete Action Plan</button>} testId="delete-plan" />
      </div>
    </AppShell>
  );
}
